import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Check, FileCheck2, FileText, Search, ShieldCheck, Sparkles } from "lucide-react";

const intentPages = {
  "contract-drafting-ai": {
    title: "AI ร่างสัญญา",
    eyebrow: "Contract drafting AI",
    h1: "AI ร่างสัญญาสำหรับงานกฎหมายไทย",
    description:
      "ใช้ KHORN AI เพื่อเริ่มร่างสัญญาจาก brief หรือ template ช่วยจัดโครงสร้าง clause ปรับภาษากฎหมาย และตรวจประเด็นสำคัญก่อนส่งต่อให้ทีมกฎหมาย",
    keywords:
      "AI ร่างสัญญา, contract drafting AI, legal drafting AI, AI เอกสารกฎหมาย, ร่างสัญญาอัตโนมัติ, KHORN AI",
    icon: FileText,
    useCases: ["สัญญาจ้างบริการ", "สัญญาเช่า", "สัญญาซื้อขาย", "NDA", "บันทึกข้อตกลง", "หนังสือบอกกล่าว"],
    steps: [
      ["ใส่ brief", "บอกประเภทสัญญา คู่สัญญา ขอบเขตงาน และเงื่อนไขสำคัญ"],
      ["สร้าง draft", "AI ช่วยวางโครงสร้างและถ้อยคำเริ่มต้นให้ตรวจต่อได้"],
      ["ตรวจข้อกฎหมาย", "ค้นกฎหมายหรือมาตราที่เกี่ยวข้องเพื่อประกอบการแก้ไข"],
      ["ส่งต่อทีม", "ใช้ร่างเป็นเอกสารตั้งต้นสำหรับ lawyer review"],
    ],
    faqs: [
      ["AI ร่างสัญญาใช้แทนทนายได้ไหม?", "ไม่ควรใช้แทนการพิจารณาของนักกฎหมาย แต่ช่วยลดเวลาทำร่างแรก จัดโครงสร้าง และเสนอถ้อยคำให้ตรวจต่อได้เร็วขึ้น"],
      ["KHORN AI ร่างสัญญาอะไรได้บ้าง?", "รองรับสัญญาทั่วไป เช่น สัญญาจ้างบริการ สัญญาเช่า สัญญาซื้อขาย NDA หนังสือบอกกล่าว และเอกสารกฎหมายอื่นตาม brief"],
      ["ต่างจาก template สัญญาอย่างไร?", "template เป็นเอกสารตายตัว แต่ KHORN AI ช่วยปรับเนื้อหาตามบริบท ขอบเขตงาน ความเสี่ยง และเงื่อนไขที่ผู้ใช้ใส่"],
    ],
  },
  "contract-review-ai": {
    title: "AI ตรวจสัญญา",
    eyebrow: "Contract review AI",
    h1: "AI ตรวจสัญญาและช่วยทำ redline",
    description:
      "ตรวจ clause สำคัญ ความเสี่ยง ภาระผูกพัน วันครบกำหนด และเสนอถ้อยคำแก้ไขในรูปแบบที่ทีมกฎหมายใช้ตรวจต่อได้",
    keywords: "AI ตรวจสัญญา, contract review AI, redline สัญญา, ตรวจ clause, ตรวจความเสี่ยงสัญญา",
    icon: FileCheck2,
    useCases: ["ตรวจความรับผิด", "ตรวจการเลิกสัญญา", "ตรวจ confidentiality", "ตรวจ payment term", "ตรวจ indemnity", "สรุปประเด็นเสี่ยง"],
    steps: [
      ["อัปโหลดหรือวางข้อความ", "นำสัญญาหรือ clause ที่ต้องการตรวจเข้ามาใน workspace"],
      ["ระบุเป้าหมายการตรวจ", "เช่น ตรวจความเสี่ยงฝ่ายผู้ว่าจ้าง หรือปรับให้เป็นกลาง"],
      ["รับข้อเสนอแก้ไข", "ดูประเด็นเสี่ยงและถ้อยคำแก้ไขแบบ redline"],
      ["ตรวจทานขั้นสุดท้าย", "ให้ทนายหรือทีมกฎหมายตัดสินใจก่อนใช้งานจริง"],
    ],
    faqs: [
      ["AI ตรวจสัญญาดูอะไรบ้าง?", "ดูเงื่อนไขสำคัญ เช่น ความรับผิด การเลิกสัญญา การชำระเงิน ขอบเขตงาน การรักษาความลับ และข้อที่อาจทำให้คู่สัญญาเสียเปรียบ"],
      ["KHORN AI ทำ redline ได้ไหม?", "แนวทางผลิตภัณฑ์รองรับการเสนอถ้อยคำแก้ไขและแสดงสิ่งที่ควรตัดหรือเติม เพื่อให้ทีมกฎหมายตรวจต่อได้"],
      ["เหมาะกับใคร?", "เหมาะกับทนายความ ที่ปรึกษากฎหมาย legal officer และทีมธุรกิจที่ต้องคัดกรองสัญญาก่อนส่งให้ผู้เชี่ยวชาญตรวจ"],
    ],
  },
  "thai-law-search": {
    title: "KHORN MCP สำหรับค้นกฎหมายไทย",
    eyebrow: "Thai law MCP",
    h1: "ต่อฐานกฎหมายไทยเข้ากับ AI workspace",
    description:
      "KHORN MCP ช่วยให้ Codex, Claude และเครื่องมือที่รองรับ MCP เรียกค้นกฎหมายไทยและฎีกาจากฐานข้อมูล KHORN เพื่อใช้เป็น reference layer",
    keywords: "KHORN MCP, Thai law MCP, legal MCP, ค้นกฎหมายไทย, ค้นฎีกา, Codex, Claude",
    icon: Search,
    useCases: ["ต่อ Codex", "ต่อ Claude", "ค้นกฎหมาย", "ค้นฎีกา", "reference layer", "MCP client"],
    steps: [
      ["เปิดหน้า KHORN MCP", "เข้า /mcp เพื่อดูภาพรวมฐานข้อมูลที่เชื่อมได้"],
      ["เพิ่ม MCP endpoint", "นำ endpoint ไปใส่ใน Codex, Claude หรือ MCP client"],
      ["เปิด law และ deka search", "ให้ AI workspace เรียกค้นแหล่งอ้างอิงจาก KHORN"],
      ["ใช้เป็น reference layer", "ตรวจแหล่งกฎหมายก่อนให้ AI ทำงานต่อ"],
    ],
    faqs: [
      ["KHORN MCP ตอบกฎหมายแทน AI ไหม?", "ไม่ใช่ MCP ทำหน้าที่เป็นชั้นข้อมูลและแหล่งอ้างอิงให้เครื่องมือ AI เรียกใช้"],
      ["เชื่อมกับอะไรได้บ้าง?", "เชื่อมกับเครื่องมือที่รองรับ MCP เช่น Codex, Claude หรือ MCP client อื่น"],
      ["มีข้อมูลอะไร?", "ครอบคลุมฐานกฎหมายไทยและฎีกาสำหรับใช้เป็นแหล่งอ้างอิง"],
    ],
    primaryHref: "/mcp",
    primaryLabel: "เปิด KHORN MCP",
  },
  "nda-drafting-ai": {
    title: "AI ร่าง NDA",
    eyebrow: "NDA drafting AI",
    h1: "AI ช่วยร่างและตรวจ NDA สำหรับงานธุรกิจ",
    description:
      "ช่วยเริ่มร่าง NDA ตรวจขอบเขตข้อมูลลับ ระยะเวลาความลับ ข้อยกเว้น การคืนหรือทำลายข้อมูล และความรับผิดที่ควรระบุให้ชัดเจน",
    keywords: "AI ร่าง NDA, ร่างสัญญา NDA, ตรวจ NDA, non disclosure agreement AI, สัญญารักษาความลับ",
    icon: FileText,
    useCases: ["NDA ฝ่ายเดียว", "Mutual NDA", "ขอบเขตข้อมูลลับ", "ข้อยกเว้น", "ระยะเวลาความลับ", "การคืนข้อมูล"],
    steps: [
      ["ระบุคู่สัญญา", "กำหนดว่าเป็น NDA ฝ่ายเดียวหรือสองฝ่าย"],
      ["ใส่บริบทข้อมูล", "บอกประเภทข้อมูลที่ต้องคุ้มครองและวัตถุประสงค์การเปิดเผย"],
      ["ร่าง clause", "AI เสนอ clause ตั้งต้นพร้อมถ้อยคำที่ชัดเจน"],
      ["ตรวจความสมดุล", "เช็กข้อจำกัด ระยะเวลา และภาระผูกพันก่อนใช้งาน"],
    ],
    faqs: [
      ["NDA ควรมีอะไรบ้าง?", "ควรมีนิยามข้อมูลลับ วัตถุประสงค์การใช้ข้อมูล ข้อยกเว้น ระยะเวลาความลับ การคืนข้อมูล และผลของการผิดสัญญา"],
      ["AI ช่วยตรวจ NDA ได้อย่างไร?", "ช่วยชี้ข้อที่กว้างเกินไป ขาดข้อยกเว้น ขาดระยะเวลา หรือขาดขั้นตอนคืนและทำลายข้อมูล"],
      ["เหมาะกับ startup ไหม?", "เหมาะสำหรับใช้เริ่มร่างก่อนส่งให้ทีมกฎหมายหรือที่ปรึกษาตรวจขั้นสุดท้าย"],
    ],
  },
  "privacy-policy-generator": {
    title: "AI ร่าง Privacy Policy",
    eyebrow: "Privacy policy generator",
    h1: "AI ช่วยร่าง Privacy Policy และประกาศความเป็นส่วนตัว",
    description:
      "ช่วยเตรียมร่างประกาศความเป็นส่วนตัวให้ครอบคลุมข้อมูลที่เก็บ วัตถุประสงค์การใช้ สิทธิของเจ้าของข้อมูล และช่องทางติดต่อ",
    keywords: "AI ร่าง privacy policy, privacy policy generator, PDPA, ประกาศความเป็นส่วนตัว, นโยบายความเป็นส่วนตัว",
    icon: ShieldCheck,
    useCases: ["เว็บไซต์", "SaaS", "LINE OA", "แบบฟอร์มลูกค้า", "นโยบายคุกกี้", "ประกาศ PDPA"],
    steps: [
      ["บอกบริบทบริการ", "ระบุว่าธุรกิจเก็บข้อมูลจากช่องทางใดและเพื่ออะไร"],
      ["ระบุประเภทข้อมูล", "เช่น ชื่อ เบอร์ อีเมล เอกสาร หรือข้อมูลการใช้งาน"],
      ["สร้างร่างประกาศ", "AI ช่วยจัดหัวข้อและถ้อยคำให้เป็นระบบ"],
      ["ตรวจให้เข้ากับธุรกิจ", "ปรับตาม flow จริงและให้ผู้เชี่ยวชาญตรวจ final"],
    ],
    faqs: [
      ["Privacy Policy ต้องมีอะไร?", "ควรมีข้อมูลที่เก็บ วัตถุประสงค์ ฐานการประมวลผล ระยะเวลาเก็บ สิทธิของเจ้าของข้อมูล และช่องทางติดต่อ"],
      ["ใช้กับ PDPA ได้ไหม?", "ใช้เป็นร่างตั้งต้นสำหรับประกาศที่เกี่ยวกับ PDPA ได้ แต่ต้องตรวจให้ตรงกับการเก็บและใช้ข้อมูลจริงของธุรกิจ"],
      ["ควร copy policy จากเว็บอื่นไหม?", "ไม่ควร เพราะแต่ละธุรกิจเก็บและใช้ข้อมูลไม่เหมือนกัน ควรร่างตาม flow จริงของตัวเอง"],
    ],
  },
  "letter-of-demand-ai": {
    title: "AI ร่างหนังสือทวงถาม",
    eyebrow: "Demand letter AI",
    h1: "AI ช่วยร่างหนังสือทวงถามและหนังสือบอกกล่าว",
    description:
      "ช่วยจัดโครงสร้างข้อเท็จจริง ข้อเรียกร้อง กำหนดเวลา และถ้อยคำทางกฎหมายสำหรับหนังสือทวงถามหรือหนังสือบอกกล่าวก่อนดำเนินการต่อ",
    keywords: "AI ร่างหนังสือทวงถาม, หนังสือบอกกล่าว, demand letter AI, หนังสือทวงหนี้, เอกสารกฎหมาย",
    icon: FileText,
    useCases: ["ทวงชำระเงิน", "บอกกล่าวผิดสัญญา", "ขอให้แก้ไขงาน", "บอกเลิกสัญญา", "แจ้งสงวนสิทธิ", "สรุปข้อเรียกร้อง"],
    steps: [
      ["สรุปข้อเท็จจริง", "ใส่เหตุการณ์ วันที่ สัญญา และหลักฐานสำคัญ"],
      ["ระบุข้อเรียกร้อง", "กำหนดว่าต้องการให้ชำระเงิน แก้ไขงาน หรือปฏิบัติตามสัญญา"],
      ["กำหนด deadline", "ใส่ระยะเวลาและผลหากไม่ปฏิบัติตาม"],
      ["ตรวจถ้อยคำ", "ปรับ tone ให้เหมาะกับการเจรจาหรือการดำเนินคดีต่อ"],
    ],
    faqs: [
      ["หนังสือทวงถามควรมีอะไร?", "ควรมีข้อเท็จจริง ข้อเรียกร้อง จำนวนเงินหรือการกระทำที่ต้องการ deadline และการสงวนสิทธิ"],
      ["AI ช่วยลดเวลาได้อย่างไร?", "ช่วยจัดโครงสร้างและร่างถ้อยคำตั้งต้นจากข้อมูลคดี ทำให้ทนายหรือทีมกฎหมายตรวจต่อได้เร็วขึ้น"],
      ["ต้องให้ทนายตรวจไหม?", "ควรให้ผู้เชี่ยวชาญตรวจเสมอ โดยเฉพาะเอกสารที่มีผลทางกฎหมายหรือใช้ก่อนดำเนินคดี"],
    ],
  },
};

const navItems = [
  ["หน้าหลัก", "/"],
  ["KHORN Web App", "/product"],
  ["KHORN LINE", "/line"],
  ["KHORN MCP", "/mcp"],
  ["ราคา", "/#pricing"],
  ["ความปลอดภัย", "/privacy"],
  ["ติดต่อ", "/contact"],
];

export const seoIntentSlugs = Object.keys(intentPages);

const AppNav = () => (
  <header className="fixed top-0 z-50 w-full border-b border-white/[0.06] bg-black/35 backdrop-blur-2xl">
    <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
      <Link to="/" className="flex items-center gap-4">
        <span className="text-3xl font-semibold leading-none tracking-tight text-white">K</span>
        <div>
          <div className="text-sm font-semibold tracking-tight text-white">KHORN AI</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/35">Thai Legal Intelligence</div>
        </div>
      </Link>
      <nav className="hidden items-center gap-8 lg:flex">
        {navItems.map(([label, href]) => (
          <a key={label} href={href} className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45 transition hover:text-white">
            {label}
          </a>
        ))}
      </nav>
      <a href="https://app.khorn-ai.com/signup?plan=elite&commitment=monthly" className="rounded-full bg-white px-5 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-black transition hover:scale-[1.02]">
        เริ่มใช้งาน
      </a>
    </div>
  </header>
);

const SeoIntentPage = () => {
  const { slug } = useParams();
  const page = intentPages[slug];

  if (!page) return <Navigate to="/" replace />;

  const Icon = page.icon;
  const url = `https://www.khorn-ai.com/${slug}`;
  const primaryHref = page.primaryHref || "https://app.khorn-ai.com/signup?plan=elite&commitment=monthly";
  const primaryLabel = page.primaryLabel || "เริ่มใช้งาน KHORN AI";
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        name: `${page.title} | KHORN AI`,
        url,
        description: page.description,
        isPartOf: {
          "@type": "WebSite",
          name: "KHORN AI",
          url: "https://www.khorn-ai.com",
        },
        about: {
          "@type": "SoftwareApplication",
          name: "KHORN AI",
          applicationCategory: "LegalTechApplication",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: page.faqs.map(([question, answer]) => ({
          "@type": "Question",
          name: question,
          acceptedAnswer: {
            "@type": "Answer",
            text: answer,
          },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "หน้าหลัก", item: "https://www.khorn-ai.com/" },
          { "@type": "ListItem", position: 2, name: page.title, item: url },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#050505] text-white selection:bg-white selection:text-black">
      <Helmet>
        <title>{page.title} | KHORN AI</title>
        <meta name="description" content={page.description} />
        <meta name="keywords" content={page.keywords} />
        <meta property="og:title" content={`${page.title} | KHORN AI`} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={url} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:72px_72px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.12),transparent_32%)]" />
        <div className="absolute bottom-0 left-0 right-0 h-[42rem] bg-[radial-gradient(circle_at_70%_85%,rgba(212,175,55,0.12),transparent_38%)]" />
      </div>

      <AppNav />

      <main className="relative z-10 mx-auto max-w-7xl px-6 pb-24 pt-32">
        <section className="grid gap-12 py-16 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
          <div>
            <div className="mb-6 flex items-center gap-3">
              <div className="h-px w-12 bg-white/20" />
              <span className="text-[10px] font-bold uppercase tracking-[0.35em] text-white/45">{page.eyebrow}</span>
            </div>
            <h1 className="max-w-5xl text-5xl font-semibold leading-tight tracking-tight md:text-7xl">{page.h1}</h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-white/52">{page.description}</p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              {primaryHref.startsWith("/") ? (
                <Link to={primaryHref} className="inline-flex h-14 items-center justify-center gap-3 rounded-full bg-white px-8 text-sm font-bold text-black transition hover:scale-[1.02]">
                  {primaryLabel}
                  <ArrowRight size={16} />
                </Link>
              ) : (
                <a href={primaryHref} className="inline-flex h-14 items-center justify-center gap-3 rounded-full bg-white px-8 text-sm font-bold text-black transition hover:scale-[1.02]">
                  {primaryLabel}
                  <ArrowRight size={16} />
                </a>
              )}
              <Link to="/product" className="inline-flex h-14 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] px-8 text-sm font-semibold text-white/75 transition hover:bg-white/[0.08]">
                ดูผลิตภัณฑ์
              </Link>
            </div>
          </div>

          <div className="rounded-[34px] border border-white/10 bg-white/[0.045] p-5 shadow-2xl backdrop-blur-2xl md:p-7">
            <div className="rounded-[26px] bg-white px-7 py-8 text-zinc-950 shadow-2xl">
              <div className="mb-8 flex items-start justify-between gap-6 border-b border-zinc-200 pb-6">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-zinc-400">KHORN AI workspace</div>
                  <h2 className="mt-3 text-3xl font-semibold tracking-tight">{page.title}</h2>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-950 text-white">
                  <Icon size={20} />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {page.useCases.map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 text-sm font-semibold text-zinc-700">
                    <Check size={15} className="text-emerald-600" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-5 py-16 md:grid-cols-4">
          {page.steps.map(([title, desc], index) => (
            <article key={title} className="rounded-[28px] border border-white/[0.07] bg-white/[0.04] p-7 backdrop-blur-xl">
              <div className="mb-8 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-sm font-bold text-black">{index + 1}</div>
              <h2 className="text-xl font-semibold">{title}</h2>
              <p className="mt-4 text-sm leading-relaxed text-white/48">{desc}</p>
            </article>
          ))}
        </section>

        <section className="rounded-[36px] border border-white/[0.07] bg-white/[0.04] p-8 md:p-10">
          <div className="mb-10 max-w-3xl">
            <div className="mb-4 flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.3em] text-white/35">
              <Sparkles size={14} />
              FAQ
            </div>
            <h2 className="text-4xl font-semibold leading-tight tracking-tight md:text-5xl">คำถามที่คนค้นหาบ่อย</h2>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {page.faqs.map(([question, answer]) => (
              <article key={question} className="rounded-2xl border border-white/[0.06] bg-black/25 p-6">
                <h3 className="text-lg font-semibold">{question}</h3>
                <p className="mt-4 text-sm leading-relaxed text-white/50">{answer}</p>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default SeoIntentPage;
